import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react";
import { ChangeEvent, ChangeEventHandler, useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { useStore } from "../../contexts/store-provider";
import { showAmountWithFormatting } from "../../helpers/common-component-helper";
import {
  getHeadquarterIdFromLocalStorage,
  getStoreIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../helpers/localstorage-helper";
import FormLoader from "../../shared-components/FormLoader/FormLoader";
import DropdownInput from "../../shared-components/input/DropdownInput";
import LanguageSlider from "../../shared-components/language-slider/language-slider";
import { formatMessage } from "../../translations/format-message";
import WeeklyReservationStatusTable from "./components/weekly-reservation-status";
import "./dashboard.css"
import preferencesStore from "../../core/stores/preferences-store";
import { formatBookingDate } from "../../helpers/date-time-helper";

const Dashboard = () => {
  const { dashboardStore, masterManagementStore } = useStore();
  const {
    GetDailyAppointmentDataListService,
    GetDailyProjectedSalesCountService,
    dailyAppointmentDataListState,
    dailyProjectedSalesCountState,
    resetDailyAppointmentDataListState,
    resetDailyProjectedSalesCountState,
    dailyAppointmentDataList,
    dailyProjectedSalesCount,
    getDailyProjectedSalesCount
  } = dashboardStore;
  const { GetAllStoreOptionService, getAllStoreOptionsAll } = masterManagementStore;
  const userType = getUserTypeFromLocalStorage();
  let storeId = getStoreIdFromLocalStorage() || 0;
  let headquarterId = getHeadquarterIdFromLocalStorage() || 0;

  const [storeIdSearch, setStoreIdSearch] = useState<number>(
    userType === userTypeEnum[userTypeEnum.Store] ? storeId : -1
  );

  const handleStoreIdSearch: ChangeEventHandler<HTMLSelectElement> =
    useCallback((event: ChangeEvent<HTMLSelectElement>) => {
      setStoreIdSearch(event.target.value as unknown as number);
    }, []);

  useEffect(() => {
    if (userType === userTypeEnum[userTypeEnum.HeadQuarter])
      GetAllStoreOptionService();
  }, []);

  useEffect(() => {
    GetDailyAppointmentDataListService(storeIdSearch, headquarterId);
    GetDailyProjectedSalesCountService(storeIdSearch, headquarterId);
    return () => {
      resetDailyAppointmentDataListState();
      resetDailyProjectedSalesCountState();
    };
  }, [storeIdSearch]);

  return (
    <>
      {(dailyAppointmentDataListState.inProgress ||
        dailyProjectedSalesCountState.inProgress ||
        masterManagementStore.inProgress) && (
          <FormLoader
            loading={
              dailyAppointmentDataListState.inProgress ||
              dailyProjectedSalesCountState.inProgress ||
              masterManagementStore.inProgress
            }
          />
        )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-sm-6 col-md-5 col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="dashboard" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="dashboard" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-sm-6 col-md-7 col-6">
            <ul className="add-list m-0">
              <li>
                <LanguageSlider />
              </li>
              <li>
                {userType !== userTypeEnum[userTypeEnum.Store] && (
                  <>
                    {storeIdSearch && (
                      <div className="input-group">
                        <div className="input-group-text d-none d-sm-flex">
                          <span role="button" onClick={() => { }}>
                            <FontAwesomeIcon icon={faSearch} />
                          </span>
                        </div>
                        <DropdownInput
                          className="form-control"
                          placeholder={formatMessage("search_by_store_name")}
                          value={storeIdSearch}
                          onChange={handleStoreIdSearch}
                          options={getAllStoreOptionsAll.map((option) => ({
                            label: option.value,
                            value: option.id,
                          }))}
                        />
                      </div>
                    )}
                  </>
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0">
        <div className="row">
          <div className="col-lg-4 col-12">
            {
              dailyProjectedSalesCount.length > 0 &&
              <table className="table table-bordered rounded align-middle border h-100">
                <thead>
                  <tr>
                    <th colSpan={3} className="fw-normal bg-primary text-start">
                      <h3 className="m-0 text-white">
                        {formatBookingDate(getDailyProjectedSalesCount.AppointmentDate, preferencesStore.getLanguage)}
                      </h3>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border bg-primary-light h2 text-primary"><FormattedMessage id="projected_sales" /></td>
                    <td className="border bg-primary-light text-center">{getDailyProjectedSalesCount.ProjectedCount} <FormattedMessage id="booking_count"/></td>
                    <td className="border bg-primary-light text-center">{showAmountWithFormatting(getDailyProjectedSalesCount.ProjectedAmount)}</td>
                  </tr>
                  <tr>
                    <td className="border bg-secondary-light h2 text-dark"><FormattedMessage id="recorded_sales" /></td>
                    <td className="border bg-secondary-light text-center text-dark">{getDailyProjectedSalesCount.RecordedCount} <FormattedMessage id="booking_count"/></td>
                    <td className="border bg-secondary-light text-center text-dark">{showAmountWithFormatting(getDailyProjectedSalesCount.RecordedAmount)}</td>
                  </tr>
                </tbody>
              </table>
            }
          </div>
          <div className="col-lg-8 col-12 mt-4 mt-lg-0">
            <div className="card">
            {
              dailyAppointmentDataList.length > 0 &&
              <WeeklyReservationStatusTable dailyAppointmentDataList={dailyAppointmentDataList} storeId={storeIdSearch}/>
            }
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default observer(Dashboard);
